import { useMemo, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import MEDIA_TYPE from 'consts/mediaType.const';

/**
 * Hook để kiểm tra xem gallery preview được mở từ đâu
 *
 * @example
 * const gallerySource = useGallerySource();
 *
 * // Kiểm tra nguồn mở
 * if (gallerySource.isFromGallery) {
 *   console.log('Mở từ gallery menu');
 * } else if (gallerySource.isFromHotspot) {
 *   console.log('Mở từ click media hotspot');
 * }
 *
 * @returns {Object}
 * @returns {string|null} returns.source - 'gallery' | 'hotspot' | null
 * @returns {boolean} returns.isFromGallery - true nếu mở từ gallery menu
 * @returns {boolean} returns.isFromHotspot - true nếu mở từ click media hotspot
 * @returns {string|null} returns.previousUrl - URL trước đó (chỉ có khi isFromHotspot = true)
 */
export const useGallerySource = () => {
  const history = useHistory();
  const { hotspotId } = useParams();
  const { media } = useSelector((state) => state.json);

  // Lưu previousUrl và fromHotspot flag ban đầu (từ scene) để giữ nguyên khi navigate giữa các gallery items
  const initialPreviousUrlRef = useRef(null);
  const initialFromHotspotRef = useRef(false);

  const result = useMemo(() => {
    const pathname = history.location.pathname;

    // Kiểm tra nếu route là /gallery hoặc /gallery/{label}
    const isGalleryRoute =
      pathname === '/gallery' || pathname.startsWith('/gallery/');

    // Kiểm tra nếu có hotspotId trong route và media type là GALLERY
    const currentMedia = hotspotId
      ? media.find((m) => m.id === hotspotId)
      : null;
    const isHotspotGallery =
      hotspotId && currentMedia && currentMedia.type === MEDIA_TYPE.GALLERY;

    // Kiểm tra flag fromHotspot từ nhiều nguồn:
    // 1. React Router state (khi dùng history.push với state)
    // 2. window.history.state (khi dùng window.history.pushState)
    // 3. Query params (fallback khi dùng window.location.href)
    const routerState = history.location.state;
    const windowHistoryState = window.history?.state;
    const searchParams = new URLSearchParams(history.location.search);

    const fromHotspotRouterState = routerState?.fromHotspot === true;
    const fromHotspotWindowState = windowHistoryState?.fromHotspot === true;
    const fromHotspotQuery = searchParams.get('fromHotspot') === 'true';

    const currentFromHotspot =
      fromHotspotRouterState || fromHotspotWindowState || fromHotspotQuery;

    // Lấy previousUrl từ state hoặc query params
    const previousUrlRouterState = routerState?.previousUrl;
    const previousUrlWindowState = windowHistoryState?.previousUrl;
    const previousUrlQuery = searchParams.get('previousUrl');
    const currentPreviousUrl =
      previousUrlRouterState ||
      previousUrlWindowState ||
      (previousUrlQuery ? decodeURIComponent(previousUrlQuery) : null);

    // Lưu previousUrl và fromHotspot flag ban đầu nếu có và chưa được lưu
    // Điều này đảm bảo khi navigate giữa các gallery items, vẫn giữ nguyên previousUrl ban đầu
    if (currentPreviousUrl && !initialPreviousUrlRef.current) {
      initialPreviousUrlRef.current = currentPreviousUrl;
    }
    if (currentFromHotspot && !initialFromHotspotRef.current) {
      initialFromHotspotRef.current = true;
    }

    // Ưu tiên dùng previousUrl và fromHotspot ban đầu (từ scene) nếu có, nếu không thì dùng current
    const previousUrl = initialPreviousUrlRef.current || currentPreviousUrl;
    const fromHotspot = initialFromHotspotRef.current || currentFromHotspot;

    // Nếu có hotspotId và media type là GALLERY, thì mở từ hotspot
    if (isHotspotGallery) {
      return {
        source: 'hotspot',
        isFromGallery: false,
        isFromHotspot: true,
        previousUrl: previousUrl,
      };
    }

    // Nếu đang ở gallery route và có flag fromHotspot, thì mở từ hotspot
    // (theo logic trong InfoTag.js: khi click media hotspot sẽ navigate với state.fromHotspot = true)
    // Sử dụng fromHotspot từ ref để đảm bảo không bị mất khi navigate giữa các gallery items
    if (isGalleryRoute && fromHotspot) {
      return {
        source: 'hotspot',
        isFromGallery: false,
        isFromHotspot: true,
        previousUrl: previousUrl,
      };
    }

    // Nếu route là gallery route và không có flag fromHotspot, thì mở từ gallery menu
    if (isGalleryRoute && !fromHotspot) {
      return {
        source: 'gallery',
        isFromGallery: true,
        isFromHotspot: false,
        previousUrl: null,
      };
    }

    return {
      source: null,
      isFromGallery: false,
      isFromHotspot: false,
      previousUrl: null,
    };
  }, [
    history.location.pathname,
    history.location.state,
    history.location.search,
    hotspotId,
    media,
  ]);

  return result;
};
